import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CalendarOptions, EventHoveringArg } from '@fullcalendar/angular';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from 'src/app/core/reducers';
import { ContactsService } from 'src/app/core/timeline';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { currentUser, isLoggedOut, User } from 'src/app/core/auth';
import { UserService } from 'src/app/core/auth/_services';
import { CategoryService } from 'src/app/core/lists';
import { StringUtilsService } from '../../../../core/services/string-utils.service';
import { FollowUpService } from '../../../../core/services/follow-up.service';
import { FollowUpDialogComponent } from '../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'kt-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
})
export class CalendarViewComponent implements OnInit {
  fromDate = new FormControl();
  toDate = new FormControl();
  parent = this;
  userCtrl = new FormControl();
  contactCtrl = new FormControl();
  entity = new FormControl();
  template = new FormControl();
  selectedUser: User;
  selectedContact: any;
  currentUser: User;
  eventClicked: boolean;
  private subscriptions: Subscription[] = [];
  searchUsers = this.userService.searchUsers.bind(this.userService);
  contactAutocompleteCall = this.contactsService.searchContactsFollowups.bind(
    this.contactsService,
    [],
  );

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    themeSystem: 'bootstrap',
    headerToolbar: { center: 'dayGridMonth,dayGridWeek,dayGridDay' }, // buttons for switching between views
    eventClick: (info) => this.handleEventClick(info.event),
    eventMouseEnter: (arg: EventHoveringArg) => {
      this.eventMouseOver(arg.event);
    },
    views: {
      dayGrid: {
        // options apply to dayGridMonth, dayGridWeek, and dayGridDay views
      },
      week: {
        // options apply to dayGridWeek and timeGridWeek views
      },
      day: {
        // options apply to dayGridDay and timeGridDay views
      },
    },
  };
  categoryId: null;
  type: '';
  categoryList = [];
  isMyFollowup = true;
  isTeamFollowups = false;

  constructor(
    private contactsService: ContactsService,
    private followUpService: FollowUpService,
    private translate: TranslateService,
    private userService: UserService,
    private store: Store<AppState>,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CalendarViewComponent>,
    private categoryService: CategoryService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.store.pipe(select(currentUser)).subscribe((res) => {
        if (res) {
          this.currentUser = res;
          this.selectedUser = this.currentUser;
        }
      }),
    );
    this.subscriptions.push(
      this.store.pipe(select(isLoggedOut)).subscribe((res) => {
        if (res) {
          this.dialogRef.close();
        }
      }),
    );
    const categorySubscription = this.categoryService.getCategories().subscribe((res) => {
      if (res) this.categoryList = res.data;
    });
    this.subscriptions.push(categorySubscription);

    this.loadFollowups();
  }

  eventMouseOver(event) {
    const text = event.event._def.extendedProps.followup.note;
    if (text) {
      $(event.el).attr('title', text);
    }
  }

  loadFollowups() {
    this.followUpService
      .getFollowUpByFilters(
        this.datePipe.transform(this.fromDate?.value, 'yyyy-MM-dd'),
        this.datePipe.transform(this.toDate?.value, 'yyyy-MM-dd', 'utc'),
        this.selectedUser ? this.selectedUser.id : '',
        this.type,
        this.isTeamFollowups,
        this.selectedContact,
      )
      .subscribe((res) => {
        const calendarOptions = Object.assign({}, this.calendarOptions);
        calendarOptions.events = res.map((e) => {
          let color = '';
          switch (e.status) {
            case 'COMPLETED':
              color = '#00aaff';
              break;
            case 'ACTIVE':
              color = '#1BC5BD';
              break;
            case 'CANCELLED':
              color = '#F64E60';
              break;
          }
          if (e.transaction_type === 'TASK' || e.transaction_type === 'WORKFLOW') {
            e.note = e.name ? e.name : e.note;
          }
          const date = new Date(e.follow_up_date + ' UTC');
          return {
            eventId: e.id,
            title: e.note,
            date,
            start: date,
            end: date,
            followup: e,
            type: e.transaction_type,
            body_id: e.transformation_id,
            backgroundColor: color,
          };
        });
        this.calendarOptions = calendarOptions;
      });
  }

  handleEventClick(event) {
    if (!this.eventClicked) {
      this.eventClicked = true;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.closeOnNavigation = true;
      dialogConfig.autoFocus = true;
      dialogConfig.width = '50vw';
      dialogConfig.data = {
        mode: 'UPDATE',
        body: null,
        type: event._def.extendedProps.type,
        title: '',
        body_id: event._def.extendedProps.body_id,
        followup: event._def.extendedProps.followup,
        calendar: true,
      };
      const dialogRef = this.dialog.open(FollowUpDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          setTimeout(() => {
            this.loadFollowups();
          });
        }
        this.eventClicked = false;
      });
    }
  }

  userSelected(value): void {
    this.selectedUser = value;
    this.loadFollowups();
  }

  onContactChange(val) {
    this.selectedContact = val && val.id ? val.id : null;
    this.loadFollowups();
  }

  fetchMyFollowups() {
    this.isTeamFollowups = false;
    this.isMyFollowup = !this.isMyFollowup;
    if (this.isMyFollowup) {
      this.selectedUser = this.currentUser;
    } else {
      this.selectedUser = null;
    }
    this.loadFollowups();
  }

  fetchFollowingTasks() {
    this.isMyFollowup = false;
    this.selectedUser = null;
    this.isTeamFollowups = !this.isTeamFollowups;
    this.loadFollowups();
  }

  displayFn(val: User): string {
    if (!val) return '';
    let res = '';
    if (val.fullName) {
      res = val.fullName;
    }
    if (val.name) {
      res += ` <small class="text-muted">${val.name}</small>`;
    }
    return res;
  }

  displayFnSelected(val: User): string {
    if (!val) return '';
    let res = '';
    if (val.fullName) {
      res = val.fullName;
    }
    return res;
  }

  close() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.dialogRef.close();
  }

  startFilter(d: Date | null): boolean {
    const date = d || new Date();
    // Prevent Saturday and Sunday from being selected.
    if (this.toDate && this.toDate.value) {
      return date.getTime() < this.toDate.value.getTime();
    }
    return true;
  }

  toFilter(d: Date | null): boolean {
    const date = d || new Date();
    // Prevent Saturday and Sunday from being selected.
    if (this.fromDate && this.fromDate.value) {
      return date.getTime() > this.fromDate.value.getTime();
    }
    return true;
  }
}
